import type { Condition, ConditionMap } from '../conditions';
import type { BaseLinkRoute, BaseRoute } from './types';

import { evaluateCondition } from '../conditions';
import { isGroupRoute, isLinkRoute } from './types';

// ============================================================================
// Path Utilities
// ============================================================================

/**
 * Checks if a path contains $param placeholders that aren't in the provided params.
 * Used to skip routes in navigation when required params are not available.
 *
 * @example
 * hasUnresolvedParams('/projects/$projectId/boards/$boardId', { projectId: '123' })
 * // Returns: true (boardId is missing)
 */
export function hasUnresolvedParams(
    path: string,
    params: Record<string, string | undefined>,
): boolean {
    const matches = path.matchAll(/\$(\w+)/g);
    for (const match of matches) {
        if (!params[match[1]]) {
            return true;
        }
    }
    return false;
}

/**
 * Resolves a route path by replacing $param placeholders with actual param values.
 *
 * @example
 * resolvePath('/projects/$projectId/details', { projectId: '123' })
 * // Returns: '/projects/123/details'
 */
export function resolvePath(
    path: string,
    params: Record<string, string | undefined>,
): string {
    let resolved = path;
    for (const [key, value] of Object.entries(params)) {
        if (value) {
            resolved = resolved.replace(`$${key}`, value);
        }
    }
    return resolved;
}

/**
 * Matches a route path pattern against a current path.
 * Handles $param placeholders in route paths.
 *
 * @example
 * matchPath('/projects/$projectId', '/projects/123', { projectId: '123' })
 * // Returns: true
 */
export function matchPath(
    routePath: string,
    currentPath: string,
    params: Record<string, string | undefined>,
): boolean {
    // Replace $param placeholders with actual values from params
    const resolvedPath = resolvePath(routePath, params);

    // Compare paths (normalize trailing slashes)
    const normalizedRoute = resolvedPath.replace(/\/$/, '');
    const normalizedCurrent = currentPath.replace(/\/$/, '');

    return normalizedRoute === normalizedCurrent;
}

/**
 * Checks if the current path matches a route or any of its child routes.
 * Used for sidebar active state when parent route has nested tabs.
 *
 * @example
 * isRouteActive(inspectionPlansRoute, '/inspection-plans/plan-of-approach', params)
 * // Returns: true (matches child route)
 */
export function isRouteActive<TContext, TConditionName extends string = string>(
    route: BaseLinkRoute<TContext, TConditionName>,
    currentPath: string,
    params: Record<string, string | undefined>,
): boolean {
    // Check if current path matches the route itself
    if (matchPath(route.path, currentPath, params)) {
        return true;
    }

    // Check if current path matches any child route
    if (route.routes) {
        return route.routes.some(child => matchPath(child.path, currentPath, params));
    }

    return false;
}

// ============================================================================
// Condition Evaluation Helper
// ============================================================================

/**
 * Evaluates a route condition, supporting both inline functions and named conditions.
 * This is a convenience wrapper that handles the optional condition field.
 *
 * @param condition - The condition to evaluate (string name, inline function, or undefined)
 * @param context - The navigation context
 * @param conditions - Map of named conditions
 * @returns true if condition passes or is undefined, false otherwise
 */
function evaluateRouteCondition<TContext, TConditionName extends string = string>(
    condition: Condition<TContext, TConditionName> | undefined,
    context: TContext,
    conditions: ConditionMap<TContext>,
): boolean {
    if (condition === undefined) {
        return true;
    }
    return evaluateCondition(condition, context, conditions);
}

// ============================================================================
// Route Filtering
// ============================================================================

/**
 * Filters routes based on their conditions.
 * Recursively filters nested routes and removes empty groups.
 *
 * Supports both inline function conditions and named string conditions.
 * Named conditions are resolved from the provided conditions map.
 *
 * @param routes - Routes to filter
 * @param context - The navigation context
 * @param conditions - Map of named conditions (optional, defaults to empty)
 */
export function filterRoutes<TContext, TConditionName extends string = string>(
    routes: BaseRoute<TContext, TConditionName>[],
    context: TContext,
    conditions: ConditionMap<TContext> = {},
): BaseRoute<TContext, TConditionName>[] {
    return routes
        .filter(route => evaluateRouteCondition(route.condition, context, conditions))
        .map((route) => {
            if (isLinkRoute(route) && route.routes) {
                return {
                    ...route,
                    routes: filterRoutes(route.routes, context, conditions) as BaseLinkRoute<TContext, TConditionName>[],
                };
            }
            if (isGroupRoute(route)) {
                return {
                    ...route,
                    routes: filterRoutes(route.routes, context, conditions),
                };
            }
            return route;
        })
        .filter((route) => {
            // Remove groups with no visible children
            if (isGroupRoute(route)) {
                return route.routes.length > 0;
            }
            return true;
        });
}

// ============================================================================
// Route Flattening
// ============================================================================

/**
 * Flattens all LinkRoutes (including nested ones) for prev/next navigation.
 * Preserves order for sequential traversal.
 *
 * Smart deduplication: If a parent route's path equals any of its child paths,
 * the parent is skipped to avoid duplicate entries that cause navigation issues.
 */
export function flattenLinkRoutes<TContext, TConditionName extends string = string>(
    routes: BaseRoute<TContext, TConditionName>[],
): BaseLinkRoute<TContext, TConditionName>[] {
    const result: BaseLinkRoute<TContext, TConditionName>[] = [];

    for (const route of routes) {
        if (isLinkRoute(route)) {
            // Check if parent path duplicates any child path
            const isDuplicateOfChild = route.routes?.some(child => child.path === route.path) ?? false;

            // Only add parent if it doesn't duplicate a child
            if (!isDuplicateOfChild) {
                result.push(route);
            }

            if (route.routes) {
                result.push(...flattenLinkRoutes(route.routes));
            }
        }
        else if (isGroupRoute(route)) {
            result.push(...flattenLinkRoutes(route.routes));
        }
    }

    return result;
}

/**
 * Flattens only top-level LinkRoutes (excludes nested tab routes).
 * Useful for sidebar navigation that only shows main pages.
 */
export function flattenSidebarRoutes<TContext, TConditionName extends string = string>(
    routes: BaseRoute<TContext, TConditionName>[],
): BaseLinkRoute<TContext, TConditionName>[] {
    const result: BaseLinkRoute<TContext, TConditionName>[] = [];

    function collectSidebarRoutes(routeList: BaseRoute<TContext, TConditionName>[]) {
        for (const route of routeList) {
            if (isLinkRoute(route)) {
                result.push(route);
            }
            else if (isGroupRoute(route)) {
                collectSidebarRoutes(route.routes);
            }
        }
    }

    collectSidebarRoutes(routes);
    return result;
}

// ============================================================================
// Header Tab Discovery
// ============================================================================

/**
 * Finds the parent LinkRoute that contains nested routes (tabs) for the current path.
 * Returns the nested routes if found, or null if not on a tabbed page.
 */
export function findHeaderTabs<TContext, TConditionName extends string = string>(
    routes: BaseRoute<TContext, TConditionName>[],
    currentPath: string,
    params: Record<string, string | undefined>,
): BaseLinkRoute<TContext, TConditionName>[] | null {
    function findTabsForPath(
        routeList: BaseRoute<TContext, TConditionName>[],
    ): BaseLinkRoute<TContext, TConditionName>[] | null {
        for (const route of routeList) {
            if (isLinkRoute(route)) {
                // Check if this route has nested routes (tabs)
                if (route.routes && route.routes.length > 0) {
                    // Check if any of the nested routes match the current path
                    const matchesNestedRoute = route.routes.some(nestedRoute =>
                        matchPath(nestedRoute.path, currentPath, params),
                    );

                    if (matchesNestedRoute) {
                        return route.routes;
                    }

                    // Also check if we're on the parent route itself but with a child route structure
                    // (handles index routes like /inspection-plans -> /inspection-plans)
                    if (matchPath(route.path, currentPath, params)) {
                        return route.routes;
                    }

                    // Recursively check nested routes
                    const nestedResult = findTabsForPath(route.routes);
                    if (nestedResult) {
                        return nestedResult;
                    }
                }
            }
            else if (isGroupRoute(route)) {
                // Search within group's routes
                const result = findTabsForPath(route.routes);
                if (result) {
                    return result;
                }
            }
        }
        return null;
    }

    return findTabsForPath(routes);
}

// ============================================================================
// Prev/Next Navigation
// ============================================================================

/**
 * Finds the prev/next routes for the current location.
 * Handles nested tab navigation with exitTo support.
 * Skips routes that have unresolved params (e.g., $distributionboardId when not on a detail page).
 */
export function findPrevNext<TContext, TConditionName extends string = string>(
    flatRoutes: BaseLinkRoute<TContext, TConditionName>[],
    currentPath: string,
    params: Record<string, string | undefined>,
): {
    prev: BaseLinkRoute<TContext, TConditionName> | null;
    next: BaseLinkRoute<TContext, TConditionName> | null;
    current: BaseLinkRoute<TContext, TConditionName> | null;
} {
    // Find current route by matching path pattern against current location
    const currentIndex = flatRoutes.findIndex(route =>
        matchPath(route.path, currentPath, params),
    );

    if (currentIndex === -1) {
        return { prev: null, next: null, current: null };
    }

    const currentRoute = flatRoutes[currentIndex];

    // Find prev route, skipping routes with unresolved params
    let prevRoute: BaseLinkRoute<TContext, TConditionName> | null = null;
    for (let i = currentIndex - 1; i >= 0; i--) {
        if (!hasUnresolvedParams(flatRoutes[i].path, params)) {
            prevRoute = flatRoutes[i];
            break;
        }
    }

    // Find next route
    let nextRoute: BaseLinkRoute<TContext, TConditionName> | null = null;

    if (currentRoute.exitTo) {
        // exitTo takes precedence - find the route matching exitTo
        nextRoute = flatRoutes.find(route =>
            matchPath(route.path, currentRoute.exitTo!, params),
        ) ?? null;
    }
    else {
        // Find next route, skipping routes with unresolved params
        for (let i = currentIndex + 1; i < flatRoutes.length; i++) {
            if (!hasUnresolvedParams(flatRoutes[i].path, params)) {
                nextRoute = flatRoutes[i];
                break;
            }
        }
    }

    return {
        prev: prevRoute,
        next: nextRoute,
        current: currentRoute,
    };
}
